'use client';

import { useEffect } from 'react';

/**
 * ServiceWorkerUnregister Component
 * 
 * This component unregisters all service workers and clears caches
 * to ensure users always get fresh content and prevent aggressive caching issues.
 */
const ServiceWorkerUnregister = () => {
  useEffect(() => {
    const unregisterServiceWorkers = async () => {
      // Check if service workers are supported in the browser
      if ('serviceWorker' in navigator) {
        try {
          // Get all service worker registrations
          const registrations = await navigator.serviceWorker.getRegistrations();

          if (registrations.length > 0) {
            console.info(`[ServiceWorker] Found ${registrations.length} service worker(s) to unregister`);

            // Unregister all service workers
            const unregisterPromises = registrations.map(async (registration) => {
              try {
                const success = await registration.unregister();
                if (success) {
                  console.info('[ServiceWorker] Successfully unregistered:', registration.scope);
                } else {
                  console.warn('[ServiceWorker] Failed to unregister:', registration.scope);
                }
                return success;
              } catch (error) {
                console.error('[ServiceWorker] Error unregistering:', registration.scope, error);
                return false;
              }
            });

            await Promise.all(unregisterPromises);
            console.info('[ServiceWorker] All service workers unregistered');
          } else {
            console.info('[ServiceWorker] No service workers found');
          }
        } catch (error) {
          console.error('[ServiceWorker] Error getting registrations:', error);
        }
      }

      // Clear all caches
      if ('caches' in window) {
        try {
          const cacheNames = await caches.keys();

          if (cacheNames.length > 0) {
            console.info(`[Cache] Found ${cacheNames.length} cache(s) to delete`);

            const deletionPromises = cacheNames.map(async (cacheName) => {
              try {
                const success = await caches.delete(cacheName);
                if (success) {
                  console.info('[Cache] Successfully deleted cache:', cacheName);
                } else {
                  console.warn('[Cache] Failed to delete cache:', cacheName);
                }
                return success;
              } catch (error) {
                console.error('[Cache] Error deleting cache:', cacheName, error);
                return false;
              }
            });

            await Promise.all(deletionPromises);
            console.info('[Cache] All caches cleared');
          } else {
            console.info('[Cache] No caches found');
          }
        } catch (error) {
          console.error('[Cache] Error clearing caches:', error);
        }
      }
    };

    // Run the unregistration immediately
    unregisterServiceWorkers();
  }, []); // Empty dependency array - run once on mount

  // This component doesn't render anything
  return null;
};

export default ServiceWorkerUnregister;

